import { useRef, useEffect, useState, useCallback } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api } from "../lib/api";
import PetitsSamouraisModule from "./PetitsSamouraisModule";
import PowerKickHerModule from "./PowerKickHerModule";
import { toast } from "sonner";
import { askConfirmName, lockError } from "../lib/confirmName";
import { Plus, Users, AlertTriangle, CheckCircle2, CalendarDays, List, CreditCard, Ban, RotateCcw, FileText, ExternalLink, Download } from "lucide-react";
import { PageHeader } from "../components/ui/kaizen";
import HealthBookDrawer from "../components/HealthBookDrawer";
import { EquipmentTick } from "../components/EquipmentTick";
import SignatureWizard from "../components/contracts/SignatureWizard";
import { BillingActionsDialog } from "../components/billing/BillingActionsDialog";
import { ContractsTrashPanel } from "../components/billing/ContractsTrashPanel";
import { DiscountModal } from "../components/contracts/DiscountModal";
import { HeartStraight, Receipt, ArrowsClockwise, PaperPlaneTilt, Signature, SealCheck, HandHeart, BookOpenText, PencilSimpleLine, ArrowCounterClockwise, Prohibit, TrashSimple, Tag } from "@phosphor-icons/react";

// Bouton d'action « pastille » : icône duotone + libellé court visible.
function ActionBtn({ icon: Icon, label, tone, onClick, disabled, testId, title }) {
  return (
    <button type="button" data-testid={testId} onClick={onClick} disabled={disabled} title={title}
      className={`action-chip group flex flex-col items-center justify-center gap-1 w-[68px] h-[54px] rounded-xl border text-[10px] font-semibold leading-none transition-all duration-150 disabled:opacity-35 disabled:cursor-not-allowed ${tone}`}>
      <Icon size={22} weight="duotone" className="transition-transform duration-150 group-hover:-translate-y-0.5 group-hover:scale-110" />
      <span className="truncate max-w-[62px]">{label}</span>
    </button>
  );
}
const TONE = {
  gold: "border-amber-500/30 bg-amber-500/5 text-amber-300 hover:bg-amber-500/15 hover:border-amber-400",
  red: "border-[#D90429]/30 bg-[#D90429]/5 text-[#ff5c7a] hover:bg-[#D90429]/15 hover:border-[#D90429]",
  green: "border-emerald-500/30 bg-emerald-500/5 text-emerald-300 hover:bg-emerald-500/15 hover:border-emerald-400",
  violet: "border-[#635BFF]/30 bg-[#635BFF]/5 text-[#a5a0ff] hover:bg-[#635BFF]/15 hover:border-[#635BFF]",
  blue: "border-sky-500/30 bg-sky-500/5 text-sky-300 hover:bg-sky-500/15 hover:border-sky-400",
  pink: "border-pink-500/30 bg-pink-500/5 text-pink-300 hover:bg-pink-500/15 hover:border-pink-400",
  zinc: "border-zinc-600/40 bg-zinc-700/10 text-zinc-300 hover:bg-zinc-600/20 hover:border-zinc-400",
  orange: "border-orange-500/30 bg-orange-500/5 text-orange-300 hover:bg-orange-500/15 hover:border-orange-400",
};

const DISCIPLINES = [
  ["muay_thai_kickboxing", "Kickboxing/Muay Thai"],
  ["jujutsu_japonais", "Jiu-jitsu Can Ryu"],
  ["jiu_jitsu_bresilien", "Jiu-Jitsu Brésilien"],
  ["jiu_jitsu_kids", "Jiu-Jitsu Kids"],
  ["petits_samourais", "Petits Samouraïs (3-6 ans)"],
  ["power_kicker", "Power Kicker"],
  ["mma", "MMA"],
  ["autre", "Autre"],
];

const GROUPES = [
  ["kids_4_7", "Petits Samouraïs Parent-Enfant (3-6 ans)"],
  ["kids_8_12", "Kids 8-12 ans"],
  ["ados_13_17", "Ados 13-17 ans"],
  ["adultes_18_plus", "Adultes 18+"],
  ["mixte", "Mixte"],
];

const STATUTS = [
  ["active", "Actif"],
  ["fusionne", "Fusionné (doublon archivé)"],
  ["archive", "Archivé (inactif 90 j — réactivable)"],
  ["essai", "Essai"],
  ["suspendu", "Suspendu"],
  ["résilié", "Résilié"],
];

// Lot 2 — durées standard du dojo (calcule la date de fin automatiquement)
const DURATIONS = [
  [4, "4 semaines"],
  [8, "8 semaines"],
  [12, "12 semaines"],
  [24, "24 semaines"],
  [52, "52 semaines (1 an)"],
];

function addWeeks(dateStr, weeks) {
  if (!dateStr || !weeks) return "";
  const d = new Date(dateStr + "T00:00:00");
  if (isNaN(d)) return "";
  d.setDate(d.getDate() + weeks * 7);
  return d.toISOString().slice(0, 10);
}

const EMPTY_PARENT = () => ({ nom: "", courriel: "", cellulaire: "", lien: "" });
const EMPTY_URGENCE = () => ({ nom: "", telephone: "", lien: "" });
const EMPTY_FORM = {
  full_name: "", age: "", date_of_birth: "", email: "", phone: "",
  parent_email: "", parent_name: "",
  parents: [{ ...EMPTY_PARENT(), lien: "père" }, { ...EMPTY_PARENT(), lien: "mère" }],
  contacts_urgence: [EMPTY_URGENCE(), EMPTY_URGENCE()],
  discipline: "muay_thai_kickboxing", secondary_discipline: "", niveau_ceinture: "", annees_pratique: "",
  groupe_age: "adultes_18_plus", sessions_per_week: 1,
  tarif_mensuel: 0, billing_cycle: "mensuel",
  statut: "active", date_debut: "", date_fin: "", notes: "", exempt_frais_equipe: false,
};

function pad2(arr, factory) {
  const a = Array.isArray(arr) ? arr.map((x) => ({ ...factory(), ...x })) : [];
  while (a.length < 2) a.push(factory());
  return a.slice(0, 2);
}

export default function StudentContractsModule() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [tab, setTabState] = useState(searchParams.get("tab") || "liste"); // 'liste' | 'calendar' | 'ps' | 'pkh'
  const setTab = (t) => { setTabState(t); setSearchParams(t === "liste" ? {} : { tab: t }, { replace: true }); };
  const [list, setList] = useState([]);
  const tableScrollRef = useRef(null);
  const [stats, setStats] = useState(null);
  const [calendar, setCalendar] = useState(null);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [durationWeeks, setDurationWeeks] = useState("");
  const [filter, setFilter] = useState({ statut: "", discipline: "" });
  const [hbContract, setHbContract] = useState(null);  // contrat sélectionné pour Carnet santé
  const [cancelContract, setCancelContract] = useState(null);  // contrat à résilier (Lot 6)
  const [editingTarif, setEditingTarif] = useState(null);      // id du contrat en édition tarif
  const [payLinkContract, setPayLinkContract] = useState(null); // fiche élève → envoi lien de paiement Tarif Dojo
  const [discountContract, setDiscountContract] = useState(null);
  const [tarifDraft, setTarifDraft] = useState("");
  const [selectedIds, setSelectedIds] = useState([]);          // envoi groupé ciblé
  const [billingDlg, setBillingDlg] = useState(null);          // {action, contractIds, payerEmail}
  const [signFilter, setSignFilter] = useState("all");         // all | signe | attente | non_envoye
  const [wizardOpen, setWizardOpen] = useState(false);
  const [tplOpen, setTplOpen] = useState(false);
  const [tplConditions, setTplConditions] = useState("");
  const navigate = useNavigate();
  const apiBase = process.env.REACT_APP_BACKEND_URL;

  const signState = (c) => c.contract_signed_at ? "signe" : (c.contract_sent_at ? "attente" : "non_envoye");
  const [sourceFilter, setSourceFilter] = useState("all");     // all | stripe_import
  const [nameQuery, setNameQuery] = useState(searchParams.get("q") || "");
  const _fold = (v) => (v || "").normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();
  const [dupes, setDupes] = useState([]);
  const [dupePlan, setDupePlan] = useState(null);
  const previewDupe = async (d) => {
    try { const r = await api.post("/adhesion/stripe-duplicates/preview", { email: d.email }); setDupePlan(r.data); }
    catch (e) { toast.error(e.response?.data?.detail || "Aperçu impossible"); }
  };
  const cancelDupe = async (sub) => {
    if (!window.confirm(`Annuler IMMÉDIATEMENT l'abonnement Stripe en double ?

${sub.name} · ${sub.product_name || sub.product} · ${Number(sub.amount).toFixed(2)} $
ID ${sub.sub_id}
Créé le ${sub.created?.slice(0, 10)}

L'abonnement conservé : ${dupePlan.keep.sub_id} (le plus ancien).
Aucun remboursement automatique — à faire dans Stripe si une double facturation a eu lieu.`)) return;
    const confirm_name = askConfirmName("Annuler un abonnement Stripe en double", sub.name || dupePlan.email);
    if (!confirm_name) return;
    try {
      const r = await api.post("/adhesion/stripe-duplicates/cancel", { email: dupePlan.email, sub_id: sub.sub_id, confirm_name });
      toast.success(`Abonnement ${sub.sub_id} annulé (${r.data.status})`);
      const p = await api.post("/adhesion/stripe-duplicates/preview", { email: dupePlan.email }); setDupePlan(p.data);
      const dd = await api.get("/adhesion/stripe-duplicates"); setDupes(dd.data.duplicates || []);
    } catch (e) { toast.error(lockError(e, "Annulation impossible")); }
  };
  const importedCount = list.filter((c) => c.source === "stripe_import").length;
  const nq = _fold(nameQuery.trim());
  const displayList = (signFilter === "all" ? list : list.filter((c) => signState(c) === signFilter))
    .filter((c) => sourceFilter === "all" || c.source === sourceFilter)
    .filter((c) => !nq || _fold(c.full_name).includes(nq) || _fold(c.email).includes(nq) || _fold(c.parent_email).includes(nq));
  const signStats = {
    signe: list.filter((c) => c.contract_signed_at).length,
    attente: list.filter((c) => !c.contract_signed_at && c.contract_sent_at).length,
    non_envoye: list.filter((c) => !c.contract_signed_at && !c.contract_sent_at).length,
  };

  const relanceNonSignes = async () => {
    if (!window.confirm("Relancer par courriel + SMS tous les élèves qui n'ont pas signé (envoyés mais non signés) ?\n\nEnvoi RÉEL.")) return;
    try {
      const r = await api.post(`/student-contracts/sign-reminders/run?dry_run=false`, { origin_url: window.location.origin });
      toast.success(`${r.data.sent} relance(s) envoyée(s) sur ${r.data.count} en attente.`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Relance impossible."); }
  };

  const openTemplate = async () => {
    try {
      const r = await api.get("/student-contracts/contract-template");
      setTplConditions((r.data.conditions || []).join("\n"));
      setTplOpen(true);
    } catch { toast.error("Impossible de charger le modèle."); }
  };
  const saveTemplate = async () => {
    const conds = tplConditions.split("\n").map((s) => s.trim()).filter(Boolean);
    if (conds.length === 0) { toast.error("Ajoute au moins une condition."); return; }
    try {
      await api.put("/student-contracts/contract-template", { conditions: conds });
      toast.success("Modèle de contrat enregistré ✓");
      setTplOpen(false);
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const toggleSelect = (id) => setSelectedIds((s) => s.includes(id) ? s.filter((x) => x !== id) : [...s, id]);
  const mergeSelected = async () => {
    const sel = list.filter((c) => selectedIds.includes(c.id));
    const names = [...new Set(sel.map((c) => (c.full_name || "").trim().toLowerCase()))];
    let allowDifferent = false;
    if (names.length !== 1) {
      if (!window.confirm(`⚠️ Les noms diffèrent : ${sel.map((c) => c.full_name).join(" / ")}.\n\nS'agit-il bien de la MÊME personne (faute de frappe / orthographe) ?\nOK = fusionner quand même · Annuler = ne rien faire`)) { return; }
      allowDifferent = true;
    }
    const subs = sel.filter((c) => c.stripe_subscription_id).length;
    if (!window.confirm(`Fusionner ${sel.length} contrats de ${sel[0].full_name} ?\n\n• Le plus récent signé/payé devient le principal\n• L'historique des doublons est regroupé dedans\n• Les doublons sont archivés (statut « fusionné »)\n• ${subs > 1 ? `${subs - 1} abonnement(s) Stripe en double seront ANNULÉS (jamais celui du principal)` : "Aucun abonnement Stripe en double à annuler"}\n\nCette action est définitive.`)) return;
    const confirm_name = askConfirmName("Fusionner des dossiers élèves", sel[0].full_name);
    if (!confirm_name) return;
    try {
      const r = await api.post("/student-contracts/merge", { contract_ids: selectedIds, allow_different_names: allowDifferent, confirm_name });
      toast.success(`Fusion réussie — ${r.data.merged.length} doublon(s) archivé(s), ${r.data.stripe_cancelled.length} abonnement(s) Stripe annulé(s)`);
      if (r.data.stripe_errors?.length) toast.error(`Stripe : ${r.data.stripe_errors.join(" · ")}`);
      setSelectedIds([]); load();
    } catch (e) { toast.error(lockError(e, "Fusion impossible")); }
  };

  const sendSelected = async () => {
    if (selectedIds.length === 0) { toast.error("Coche au moins un élève."); return; }
    if (!window.confirm(`Envoyer le contrat + lien de paiement/signature aux ${selectedIds.length} élève(s) sélectionné(s) ?\n\nEnvoi RÉEL par courriel.`)) return;
    try {
      const r = await api.post("/student-contracts/send-contract-bulk", { origin_url: window.location.origin, contract_ids: selectedIds });
      toast.success(`${r.data.sent} envoyé(s) · ${r.data.skipped_no_email} ignoré(s) (sans courriel).`);
      setSelectedIds([]);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Envoi impossible."); }
  };

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (filter.statut) params.append("statut", filter.statut);
    if (filter.discipline) params.append("discipline", filter.discipline);
    const qs = params.toString() ? `?${params.toString()}` : "";
    api.get(`/student-contracts${qs}`).then(r => setList(r.data)).catch(() => {});
    api.get("/student-contracts/stats").then(r => setStats(r.data)).catch(() => {});
    api.get("/student-contracts/calendar").then(r => setCalendar(r.data)).catch(() => {});
  }, [filter.statut, filter.discipline]);

  useEffect(() => { load(); }, [load]);
  const [dupIds, setDupIds] = useState([]);
  useEffect(() => { api.get("/student-contracts/stripe-duplicate-alerts").then((r) => setDupIds(r.data.probable_ids || [])).catch(() => {}); }, [list.length]);
  useEffect(() => { api.get("/adhesion/stripe-duplicates").then((r) => setDupes(r.data.duplicates || [])).catch(() => {}); }, []);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = { ...form };
      if (payload.age === "") delete payload.age; else payload.age = parseInt(payload.age);
      if (payload.annees_pratique === "") delete payload.annees_pratique;
      else payload.annees_pratique = parseFloat(payload.annees_pratique);
      payload.sessions_per_week = parseInt(payload.sessions_per_week);
      payload.tarif_mensuel = parseFloat(payload.tarif_mensuel);
      ["date_of_birth", "date_debut", "date_fin", "email", "phone", "parent_email", "parent_name", "niveau_ceinture", "secondary_discipline"]
        .forEach(k => { if (!payload[k]) delete payload[k]; });
      // Parents & contacts d'urgence : ne garder que les entrées remplies
      payload.parents = (form.parents || []).filter(p => p.nom || p.courriel || p.cellulaire);
      payload.contacts_urgence = (form.contacts_urgence || []).filter(c => c.nom || c.telephone);

      if (editingId) {
        await api.patch(`/student-contracts/${editingId}`, payload);
        toast.success("Contrat mis à jour");
      } else {
        await api.post("/student-contracts", payload);
        toast.success("Élève ajouté");
      }
      setForm(EMPTY_FORM); setDurationWeeks(""); setOpen(false); setEditingId(null); load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur");
    }
  };

  const edit = (c) => {
    setEditingId(c.id); setDurationWeeks("");
    setForm({
      ...EMPTY_FORM,
      ...c,
      age: c.age ?? "",
      annees_pratique: c.annees_pratique ?? "",
      date_of_birth: c.date_of_birth ?? "",
      date_debut: c.date_debut ?? "",
      date_fin: c.date_fin ?? "",
      parents: pad2(c.parents, EMPTY_PARENT),
      contacts_urgence: pad2(c.contacts_urgence, EMPTY_URGENCE),
    });
    setOpen(true);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const remove = async (id, name) => {
    if (!window.confirm(`Supprimer le contrat de ${name} ?\n\nIl part dans la Corbeille contrats (restaurable 30 jours). Stripe n'est pas touché.`)) return;
    try {
      await api.delete(`/student-contracts/${id}`);
      toast.success("Contrat déplacé dans la corbeille (30 jours)");
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
  };

  const revokeCancellation = async (c) => {
    if (!window.confirm(`Annuler la résiliation de ${c.full_name} et réactiver son contrat ?`)) return;
    try {
      await api.post(`/student-contracts/${c.id}/cancel/revoke`);
      toast.success("Résiliation annulée — contrat réactivé.");
      load();
    } catch { toast.error("Erreur"); }
  };

  const sendContractPayment = async (c) => {
    if (!c.email && !c.parent_email) {
      toast.error("Ajoute d'abord un courriel à cet élève.");
      return;
    }
    if (!window.confirm(`Envoyer à ${c.full_name} le contrat d'adhésion (PDF) + le lien d'activation du paiement ?\n\nUn seul courriel sera envoyé (via Resend).`)) return;
    try {
      const r = await api.post(`/student-contracts/${c.id}/send-contract-and-payment`, { origin_url: window.location.origin });
      toast.success(`Contrat + paiement envoyés à ${r.data.email} ✓`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Envoi impossible.");
    }
  };

  const confirmActivated = async (c) => {
    if (!window.confirm(`Confirmer manuellement que « ${c.full_name} » est activé ? (ex : payé en personne / virement)`)) return;
    try {
      await api.post(`/adhesion/migration/confirm`, { contract_id: c.id });
      toast.success(`${c.full_name} marqué comme activé ✓`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const startEditTarif = (c) => {
    setEditingTarif(c.id);
    setTarifDraft(c.tarif_mensuel != null ? String(c.tarif_mensuel) : "");
  };
  const saveTarif = async (c) => {
    const val = parseFloat(tarifDraft);
    if (isNaN(val) || val < 0) { toast.error("Tarif invalide."); return; }
    try {
      await api.patch(`/student-contracts/${c.id}`, { tarif_mensuel: val });
      toast.success(`Tarif de ${c.full_name} : ${val.toFixed(2)} $`);
      setEditingTarif(null);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur");
    }
  };

  const subscriptionLink = async (c) => {
    if (!c.tarif_mensuel || c.tarif_mensuel <= 0) {
      toast.error("Configure d'abord un tarif mensuel pour cet élève.");
      return;
    }
    if (!window.confirm(`Créer un lien d'ABONNEMENT MENSUEL pour ${c.full_name} (${c.tarif_mensuel.toFixed(2)} $ CAD / mois) ?\n\nEnvoie ce lien à l'élève : il entre sa carte une fois et sera prélevé chaque mois automatiquement.`)) return;
    try {
      const r = await api.post(`/stripe/subscription-link/${c.id}`, { origin_url: window.location.origin });
      try { await navigator.clipboard.writeText(r.data.checkout_url); } catch (_) {}
      window.open(r.data.checkout_url, "_blank", "noopener");
      toast.success("Lien d'abonnement créé et copié ! Envoie-le à l'élève (ouvert aussi dans un onglet).");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur Stripe");
    }
  };

  const chargeMonth = async (c) => {
    if (!c.tarif_mensuel || c.tarif_mensuel <= 0) {
      toast.error("Configure d'abord un tarif mensuel pour cet élève.");
      return;
    }
    // Récupère les frais d'inscription applicables (non payés) pour afficher la ventilation
    let feeAmount = 0, feeApplicable = false;
    try {
      const fs = await api.get(`/equipment/fee-status/${c.id}`);
      if (fs.data?.applicable && fs.data?.status === "non_paye") {
        feeAmount = Number(fs.data.amount || 0);
        feeApplicable = feeAmount > 0;
      }
    } catch (e) { /* frais optionnels — on continue sans */ }
    const total = Number(c.tarif_mensuel) + feeAmount;
    const fmt = (n) => `${Number(n).toFixed(2)} $ CAD`;
    const breakdown = feeApplicable
      ? `Abonnement mensuel : ${fmt(c.tarif_mensuel)}\nFrais d'inscription : ${fmt(feeAmount)}\n──────────────\nTotal à payer : ${fmt(total)}`
      : `Total à payer : ${fmt(c.tarif_mensuel)}`;
    if (!window.confirm(`Facturer ${c.full_name} (mois courant) ?\n\n${breakdown}\n\nUne fenêtre Stripe va s'ouvrir.`)) return;
    try {
      const r = await api.post(`/stripe/charge-month/${c.id}`, { origin_url: window.location.origin });
      window.open(r.data.checkout_url, "_blank", "noopener");
      if (r.data.registration_fee > 0) {
        toast.success(`Checkout Stripe ouvert · abonnement ${fmt(r.data.base_tarif)} + frais ${fmt(r.data.registration_fee)} = ${fmt(r.data.total)}.`);
      } else {
        toast.success("Checkout Stripe ouvert dans un nouvel onglet.");
      }
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur Stripe");
    }
  };

  const [welcomeContract, setWelcomeContract] = useState(null);
  const [welcomeAmb, setWelcomeAmb] = useState("KAI");
  const [welcomeSending, setWelcomeSending] = useState(false);

  const AMBASSADORS = [
    { key: "KAI", name: "KAI", tagline: "Le guerrier KAIZEN", accent: "#c9a961" },
    { key: "MEI", name: "MEI", tagline: "PowerKick Her", accent: "#ec4899" },
    { key: "LANA", name: "LANA", tagline: "La force de la communauté", accent: "#a855f7" },
  ];

  const autoAmbassador = (discipline) => {
    const d = (discipline || "").toLowerCase();
    if (d.includes("powerkick") || d.includes("her")) return "MEI";
    if (["kick", "muay", "bjj", "jiu", "juj", "ryu", "can", "karate", "boxe", "self", "grappl", "comp", "art"].some((k) => d.includes(k))) return "KAI";
    return "LANA";
  };

  const openWelcome = (c) => {
    setWelcomeContract(c);
    setWelcomeAmb(autoAmbassador(c.discipline));
  };

  const sendWelcome = async () => {
    if (!welcomeContract) return;
    setWelcomeSending(true);
    try {
      const { data } = await api.post("/welcome/ambassador/send", {
        contract_id: welcomeContract.id, ambassador: welcomeAmb,
      });
      if (data.email_status === "envoyé") toast.success(`💛 Bienvenue de ${data.ambassador} envoyée à ${welcomeContract.full_name} (courriel + Espace Membre).`);
      else if (data.email_status === "aucun_courriel") toast.success(`💛 Bienvenue de ${data.ambassador} ajoutée à l'Espace Membre (aucun courriel au dossier).`);
      else toast.warning(`Carte ajoutée, mais l'envoi du courriel a échoué.`);
      setWelcomeContract(null);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erreur envoi bienvenue");
    } finally {
      setWelcomeSending(false);
    }
  };

  // Auto-poll on return from Stripe success_url (URL params)
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sid = params.get("stripe_session");
    const cid = params.get("contract");
    if (sid && cid) {
      api.get(`/stripe/status/${sid}`).then((r) => {
        if (r.data.payment_status === "paid") {
          toast.success("Paiement confirmé · contrat à jour pour 30 jours.");
          load();
        } else {
          toast.info("Paiement en attente de confirmation Stripe.");
        }
        // Nettoie l'URL
        window.history.replaceState({}, "", "/app/eleves");
      }).catch(() => {});
    }
    if (params.get("stripe_cancelled")) {
      toast.info("Paiement Stripe annulé.");
      window.history.replaceState({}, "", "/app/eleves");
    }
    if (params.get("sub_success")) {
      toast.success("Abonnement créé ! Clique « Synchroniser Stripe » pour le rattacher au contrat.");
      window.history.replaceState({}, "", "/app/eleves");
    }
    if (params.get("sub_cancelled")) {
      toast.info("Création d'abonnement annulée.");
      window.history.replaceState({}, "", "/app/eleves");
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div data-testid="student-contracts-page">
      <div className="px-8 lg:px-12 pt-8">
        <PageHeader
          title="Contrats Élèves"
          icon={Users}
          breadcrumbs={[{ label: "Dojo & élèves" }, { label: "Contrats Élèves" }]}
          subtitle="Suivi par élève : discipline · ceinture · âge · fréquence · tarif · statut paiement."
          actions={
            <>
              <button
                data-testid="sync-stripe-btn"
                onClick={async () => {
                  try {
                    const r = await api.post("/adhesion/stripe-sync");
                    toast.success(`${r.data.linked} relié(s) · ${r.data.active} actif(s), ${r.data.past_due} en échec, ${r.data.canceled} annulé(s) · ${r.data.tarifs_updated || 0} tarif(s) corrigé(s) depuis Stripe.`);
                    const imp = r.data.imported || [], att = r.data.attached || [];
                    if (imp.length) toast.success(`🆕 ${imp.length} abonnement(s) Stripe importé(s) comme contrat : ${imp.map((i) => i.name).join(", ")}`, { duration: 9000 });
                    if (att.length) toast.info(`🔗 ${att.length} abonnement(s) rattaché(s) par courriel : ${att.map((a) => a.name).join(", ")}`, { duration: 9000 });
                    setDupes(r.data.duplicates || []);
                    if ((r.data.duplicates || []).length) toast.warning(`⚠️ ${r.data.duplicates.length} courriel(s) avec PLUSIEURS abonnements Stripe actifs — voir le bandeau.`, { duration: 12000 });
                    load();
                  } catch (e) { toast.error("Échec de la synchro Stripe."); }
                }}
                className="px-4 py-2.5 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 text-sm flex items-center gap-2"
                title="Met à jour le statut d'abonnement Stripe sur chaque contrat (actif / en échec / annulé)"
              >
                <CreditCard size={16}/> Synchroniser Stripe
              </button>
              <button
                data-testid="recover-unfinalized-btn"
                onClick={async () => {
                  if (!window.confirm("Récupérer les inscriptions PAYÉES chez Stripe mais invisibles ici (client parti sans revenir sur la page « Merci ») ?\n\nJe vérifie chaque inscription en attente auprès de Stripe et je crée le membre + contrat pour celles réellement payées.")) return;
                  try {
                    toast.loading("Récupération en cours auprès de Stripe…", { id: "recover" });
                    const r = await api.post("/adhesion/recover-unfinalized");
                    const names = (r.data.recovered || []).map((x) => x.name).filter(Boolean).slice(0, 5).join(", ");
                    toast.success(`${r.data.recovered_count} inscription(s) payée(s) récupérée(s)${names ? " : " + names : ""}. ${r.data.still_pending} encore en attente, ${r.data.expired} expirée(s).`, { id: "recover", duration: 8000 });
                    load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Échec de la récupération.", { id: "recover" }); }
                }}
                className="px-4 py-2.5 rounded-lg border border-emerald-700 bg-emerald-950/30 hover:bg-emerald-950/50 text-emerald-200 text-sm flex items-center gap-2"
                title="Retrouve les inscriptions payées mais non finalisées (ex. client parti après le paiement) et les rend visibles ici"
              >
                <CreditCard size={16}/> 🛟 Récupérer inscriptions payées
              </button>
              <button
                data-testid="enable-tax-btn"
                onClick={async () => {
                  if (!window.confirm("Activer la taxe automatique (TPS 5% + TVQ 9,975%) sur TOUS les abonnements Stripe actifs ?\n\nPré-requis : Stripe Tax doit être activé dans ton Dashboard Stripe (avec tes inscriptions TPS/TVQ). Les abonnements sans adresse client seront ignorés.")) return;
                  try {
                    await api.post("/stripe/enable-tax-existing");
                    toast.loading("Activation des taxes en cours (traitement en arrière-plan)…", { id: "tax" });
                    let tries = 0;
                    const poll = setInterval(async () => {
                      tries++;
                      try {
                        const s = (await api.get("/stripe/enable-tax-status")).data;
                        if (s.status === "done" || s.status === "error" || tries > 40) {
                          clearInterval(poll);
                          if (s.status === "error") {
                            toast.error(`Erreur Stripe : ${s.error || "inconnue"}`, { id: "tax", duration: 9000 });
                          } else {
                            toast.success(`✅ ${s.enabled} abonnement(s) taxé(s) · ${s.already} déjà OK · ${s.skipped_no_address} sans adresse · ${s.errors} erreur(s) (sur ${s.total}).`, { id: "tax", duration: 10000 });
                          }
                        } else {
                          toast.loading(`Activation… ${s.enabled || 0} traité(s) sur ${s.total || 0}`, { id: "tax" });
                        }
                      } catch { clearInterval(poll); toast.error("Suivi interrompu", { id: "tax" }); }
                    }, 3000);
                  } catch (e) { toast.error(e?.response?.data?.detail || "Échec du lancement.", { id: "tax" }); }
                }}
                className="px-4 py-2.5 rounded-lg border border-emerald-700 bg-emerald-900/20 hover:bg-emerald-900/40 text-emerald-200 text-sm flex items-center gap-2"
                title="Active la taxe automatique TPS+TVQ (Stripe Tax) sur les abonnements existants"
              >
                <CreditCard size={16}/> Activer les taxes (TPS+TVQ)
              </button>
              <button
                data-testid="past-due-reminders-btn"
                onClick={async () => {
                  if (!window.confirm("Envoyer un courriel de relance à tous les élèves dont le paiement Stripe est en échec ?")) return;
                  try {
                    const r = await api.post("/adhesion/run-past-due-reminders");
                    toast.success(`${r.data.sent} relance(s) envoyée(s) sur ${r.data.checked} élève(s) en échec de paiement.`);
                  } catch (e) { toast.error("Échec de l'envoi des relances."); }
                }}
                className="px-4 py-2.5 rounded-lg border border-zinc-700 bg-zinc-900 hover:bg-zinc-800 text-zinc-100 text-sm flex items-center gap-2"
                title="Relance par courriel les élèves dont le prélèvement a échoué (carte à mettre à jour)"
              >
                <AlertTriangle size={16}/> Relancer impayés
              </button>
              <button
                data-testid="activation-shortcut-btn"
                onClick={() => navigate("/app/activation-membres")}
                className="px-4 py-2.5 rounded-lg border border-emerald-700 bg-emerald-900/30 hover:bg-emerald-900/50 text-emerald-200 text-sm flex items-center gap-2"
                title="Aller à la page Activation des clients — fermer Zen Planner"
              >
                <ExternalLink size={16}/> Activation membres
              </button>
              <button
                data-testid="contract-template-btn"
                onClick={openTemplate}
                className="px-4 py-2.5 rounded-lg border border-border hover:bg-secondary text-sm flex items-center gap-2"
                title="Modifier le texte des conditions du contrat"
              >
                <FileText size={16}/> Modèle de contrat
              </button>
              {signStats.attente > 0 && (
                <button
                  data-testid="relance-non-signes-btn"
                  onClick={relanceNonSignes}
                  className="px-4 py-2.5 rounded-lg border border-amber-600 bg-amber-900/20 hover:bg-amber-900/40 text-amber-300 text-sm flex items-center gap-2"
                  title="Relancer (courriel + SMS) les élèves envoyés mais non signés"
                >
                  <RotateCcw size={16}/> Relancer non-signés ({signStats.attente})
                </button>
              )}
              {selectedIds.length >= 2 && (
                <button
                  data-testid="merge-selected-btn"
                  onClick={mergeSelected}
                  className="px-4 py-2.5 rounded-lg bg-[#7c3aed] hover:bg-[#6d28d9] text-white text-sm flex items-center gap-2"
                >
                  ⛓ Fusionner {selectedIds.length} contrats (même personne)
                </button>
              )}
              {selectedIds.length >= 2 && (
                <button
                  data-testid="billing-merge-selected-btn"
                  onClick={() => { const sel = list.filter((c) => selectedIds.includes(c.id)); setBillingDlg({ action: "merge", contractIds: selectedIds, payerEmail: sel[0]?.payer_email || sel[0]?.parent_email || sel[0]?.email }); }}
                  className="px-4 py-2.5 rounded-lg bg-emerald-700 hover:bg-emerald-600 text-white text-sm flex items-center gap-2"
                  title="Famille / 2 disciplines : 1 seul abonnement Stripe, 1 facture par mois (les contrats restent distincts)"
                >
                  <CreditCard size={16}/> 1 facture Stripe pour {selectedIds.length} contrats
                </button>
              )}
              {selectedIds.length > 0 && (
                <button
                  data-testid="send-selected-btn"
                  onClick={sendSelected}
                  className="px-4 py-2.5 rounded-lg bg-[#3b82f6] hover:bg-[#2563eb] text-white text-sm flex items-center gap-2"
                >
                  <FileText size={16}/> Envoyer aux {selectedIds.length} sélectionné(s)
                </button>
              )}
              <button
                data-testid="add-contract-btn"
                onClick={() => { setOpen(!open); setEditingId(null); setForm(EMPTY_FORM); setDurationWeeks(""); }}
                className="bg-amber-500 hover:bg-amber-400 text-zinc-950 px-5 py-2.5 rounded-lg text-sm font-bold flex items-center gap-2 transition-colors active:scale-[0.98]"
              >
                <Plus size={16}/> {open ? "Annuler" : "Ajouter un élève"}
              </button>
            </>
          }
        />
        {dupes.length > 0 && (
          <div className="mb-6 rounded-xl border border-amber-500/50 bg-amber-500/10 p-4" data-testid="stripe-duplicates-banner">
            <div className="font-bold text-amber-300 flex items-center gap-2"><AlertTriangle size={16}/> Doublons Stripe — {dupes.length} courriel(s) avec plusieurs abonnements actifs (risque de double facturation)</div>
            <ul className="mt-2 text-sm text-zinc-200 space-y-1">
              {dupes.map((d) => (
                <li key={d.email} data-testid={`dupe-${d.email}`}>
                  <span className={`text-[10px] px-1.5 py-0.5 rounded mr-2 ${d.severity === "doublon_probable" ? "bg-rose-600 text-white" : "bg-zinc-700 text-zinc-200"}`}>{d.severity === "doublon_probable" ? "DOUBLON PROBABLE (même produit, même montant)" : "À vérifier (famille / 2 disciplines ?)"}</span>
                  <b>{d.email}</b> — {d.count} abonnements : {d.subs.map((x) => `${x.name || "?"} · ${x.product || "produit ?"} · ${Number(x.amount || 0).toFixed(2)} $`).join(" | ")}
                  {d.severity === "doublon_probable" && <button onClick={() => previewDupe(d)} className="ml-2 text-xs px-2 py-0.5 rounded bg-rose-600 text-white" data-testid={`dupe-preview-${d.email}`}>Nettoyer (aperçu)</button>}
                  <a href={`https://dashboard.stripe.com/customers/${d.customer_id || ""}`} target="_blank" rel="noreferrer" className="ml-2 underline text-amber-200">Ouvrir dans Stripe</a>
                </li>
              ))}
            </ul>
            {dupePlan && (
              <div className="mt-3 rounded-lg border border-rose-500/50 bg-black/40 p-3 text-sm" data-testid="dupe-plan">
                <div className="font-semibold text-rose-200">Aperçu du nettoyage — {dupePlan.email}</div>
                <p className="text-xs text-zinc-400 mt-1">On conserve le plus ancien ; les autres peuvent être annulés un à un, avec confirmation. Rien n'est fait sans clic.</p>
                {dupePlan.detail_error && <p className="text-xs text-rose-300 mt-1" data-testid="dupe-plan-error">⚠️ {dupePlan.detail_error}</p>}
                <ul className="mt-2 space-y-1">
                  {dupePlan.subs.map((s) => (
                    <li key={s.sub_id} className="flex flex-wrap items-center gap-2" data-testid={`dupe-sub-${s.sub_id}`}>
                      <span className={`text-[10px] px-1.5 py-0.5 rounded ${s.sub_id === dupePlan.keep?.sub_id ? "bg-emerald-600 text-white" : s.status === "canceled" ? "bg-zinc-700" : "bg-rose-700 text-white"}`}>{s.sub_id === dupePlan.keep?.sub_id ? "CONSERVÉ" : s.status === "canceled" ? "ANNULÉ" : "DOUBLON"}</span>
                      <span>{s.name} · {s.product_name || s.product} · {Number(s.amount).toFixed(2)} $ · créé {s.created?.slice(0, 10)} · prochain {s.current_period_end?.slice(0, 10) || "—"} · {s.status}</span>
                      {s.sub_id !== dupePlan.keep?.sub_id && s.status !== "canceled" && <button onClick={() => cancelDupe(s)} className="text-xs px-2 py-0.5 rounded bg-rose-600 text-white" data-testid={`dupe-cancel-${s.sub_id}`}>Annuler ce doublon</button>}
                    </li>
                  ))}
                </ul>
                <button onClick={() => setDupePlan(null)} className="mt-2 text-xs text-zinc-400 hover:text-white">Fermer l'aperçu</button>
              </div>
            )}
            <button onClick={() => setDupes([])} className="mt-2 text-xs text-zinc-400 hover:text-white" data-testid="dupes-dismiss">Masquer</button>
          </div>
        )}
      </div>

      {/* STATS */}
      {stats && (
        <div className="hard-border-b grid grid-cols-2 md:grid-cols-5 gap-0">
          {[
            { label: "Total", value: stats.total, icon: Users, color: "text-foreground" },
            { label: "Actifs", value: stats.actifs, icon: CheckCircle2, color: "text-[#10b981]" },
            { label: "Suspendus", value: stats.suspendus, icon: AlertTriangle, color: "text-[#f59e0b]" },
            { label: "En retard", value: stats.en_retard, icon: AlertTriangle, color: "text-[#D90429]" },
            { label: "Revenu/mois (théo)", value: `${stats.revenu_mensuel_theo.toFixed(0)} $`, icon: null, color: "text-[#c9a961] font-mono" },
          ].map((s) => (
            <div key={s.label} className="hard-border-r px-6 py-5" data-testid={`stat-${s.label.toLowerCase().replace(/\s+/g, '-')}`}>
              <div className="ticker text-muted-foreground mb-1">{s.label}</div>
              <div className={`text-2xl font-black ${s.color}`}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* FILTRES */}
      <div className="hard-border-b px-8 lg:px-12 py-3 flex items-center gap-3 flex-wrap bg-secondary/30">
        <button data-testid="tab-liste" onClick={() => setTab("liste")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "liste" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <List size={14}/> Liste
        </button>
        <button data-testid="tab-calendar" onClick={() => setTab("calendar")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "calendar" ? "bg-foreground text-white" : "hover:bg-secondary"}`}>
          <CalendarDays size={14}/> Calendrier renouvellements
        </button>
        <button data-testid="tab-ps" onClick={() => setTab("ps")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "ps" ? "bg-foreground text-white" : "hover:bg-secondary"}`} title="Programme Petits Samouraïs (3-6 ans) : cohortes, essais, espace parent, équipement">
          🥋 Petits Samouraïs
        </button>
        <button data-testid="tab-pkh" onClick={() => setTab("pkh")} className={`ticker px-3 py-1.5 flex items-center gap-2 transition-colors ${tab === "pkh" ? "bg-foreground text-white" : "hover:bg-secondary"}`} title="Programme PowerKick Her (100 % femmes) : sessions, préinscriptions, sondages, carnet">
          🥊 PowerKick Her
        </button>
        {tab === "liste" && (
          <>
            <span className="ticker text-muted-foreground ml-4 basis-full lg:basis-auto">FILTRES</span>
            <input data-testid="filter-name" value={nameQuery} onChange={(e) => setNameQuery(e.target.value)} placeholder="🔍 Nom ou courriel…"
              className="hard-border px-3 py-1.5 bg-white text-sm w-56" aria-label="Rechercher un élève" />
            <select data-testid="filter-statut" value={filter.statut} onChange={(e) => setFilter({ ...filter, statut: e.target.value })} className="hard-border px-3 py-1.5 bg-white text-sm">
              <option value="">Tous statuts</option>
              {STATUTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <button type="button" data-testid="filter-petits-samourais" onClick={() => setFilter({ ...filter, discipline: filter.discipline === "petits_samourais" ? "" : "petits_samourais" })}
              className={`hard-border px-3 py-1.5 text-sm font-semibold ${filter.discipline === "petits_samourais" ? "bg-amber-400 text-black" : "bg-white text-black hover:bg-amber-100"}`} title="Voir seulement la cohorte Petits Samouraïs (3-6 ans)">🥋 Petits Samouraïs</button>
            <select data-testid="filter-discipline" value={filter.discipline} onChange={(e) => setFilter({ ...filter, discipline: e.target.value })} className="hard-border px-3 py-1.5 bg-white text-sm">
              <option value="">Toutes disciplines</option>
              {DISCIPLINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
            <select data-testid="filter-signature" value={signFilter} onChange={(e) => setSignFilter(e.target.value)} className="hard-border px-3 py-1.5 bg-white text-sm">
              <option value="all">Toutes signatures</option>
              <option value="signe">✓ Signées ({signStats.signe})</option>
              <option value="attente">⏳ En attente ({signStats.attente})</option>
              <option value="non_envoye">Non envoyées ({signStats.non_envoye})</option>
            </select>
            <select data-testid="filter-source" value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)} className="hard-border px-3 py-1.5 bg-white text-sm">
              <option value="all">Toutes sources</option>
              <option value="stripe_import">🆕 Importés de Stripe ({importedCount}) — à compléter</option>
            </select>
          </>
        )}
      </div>

      {/* STATS SIGNATURES */}
      {tab === "liste" && (
        <div className="px-8 lg:px-12 pt-4"><ContractsTrashPanel light onRestored={load} /></div>
      )}
      {tab === "liste" && (
        <div className="px-8 lg:px-12 pt-4 flex flex-wrap gap-3" data-testid="signature-stats">
          <div className="hard-border px-4 py-2 bg-[#10b981]/10" data-testid="stat-signe"><span className="ticker text-muted-foreground">Signés</span> <b className="text-[#10b981] ml-2">{signStats.signe}</b></div>
          <div className="hard-border px-4 py-2 bg-amber-500/10" data-testid="stat-attente"><span className="ticker text-muted-foreground">En attente</span> <b className="text-amber-500 ml-2">{signStats.attente}</b></div>
          <div className="hard-border px-4 py-2" data-testid="stat-non-envoye"><span className="ticker text-muted-foreground">Non envoyés</span> <b className="ml-2">{signStats.non_envoye}</b></div>
          <button onClick={() => setWizardOpen(true)} data-testid="open-signature-wizard"
            className="hard-border px-4 py-2 bg-zinc-950 text-white text-xs font-bold uppercase tracking-wide flex items-center gap-2 hover:bg-[#D90429] transition-colors">
            <Signature size={16} weight="duotone" /> Signatures une par une ({signStats.attente + signStats.non_envoye})
          </button>
        </div>
      )}
      {wizardOpen && <SignatureWizard onClose={() => { setWizardOpen(false); load(); }} onSent={load} />}
      <BillingActionsDialog open={!!billingDlg} onClose={() => setBillingDlg(null)} action={billingDlg?.action} contractIds={billingDlg?.contractIds || []} payerEmail={billingDlg?.payerEmail} onDone={() => { setSelectedIds([]); load(); }} />

      {/* FORMULAIRE */}
      {open && (
        <form onSubmit={submit} className="hard-border-b px-8 lg:px-12 py-6 grid grid-cols-1 md:grid-cols-3 gap-3 bg-secondary/20" data-testid="contract-form">
          <input data-testid="f-name" required placeholder="Nom complet de l'élève *" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="hard-border px-3 py-2 md:col-span-2"/>
          <input data-testid="f-age" type="number" min="2" max="100" placeholder="Âge" value={form.age} onChange={(e) => setForm({ ...form, age: e.target.value })} className="hard-border px-3 py-2"/>

          <select data-testid="f-discipline" value={form.discipline} onChange={(e) => setForm({ ...form, discipline: e.target.value })} className="hard-border px-3 py-2 bg-white">
            {DISCIPLINES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <select data-testid="f-secondary-discipline" value={form.secondary_discipline} onChange={(e) => setForm({ ...form, secondary_discipline: e.target.value })} className="hard-border px-3 py-2 bg-white" title="2e discipline → rabais -25% équipement">
            <option value="">2e discipline (rabais −25% kit) — aucune</option>
            {DISCIPLINES.filter(([v]) => v !== form.discipline).map(([v, l]) => <option key={v} value={v}>+ {l}</option>)}
          </select>
          <input data-testid="f-ceinture" placeholder="Ceinture / Niveau (ex: Jaune, Bleue)" value={form.niveau_ceinture} onChange={(e) => setForm({ ...form, niveau_ceinture: e.target.value })} className="hard-border px-3 py-2"/>
          <input data-testid="f-annees" type="number" step="0.5" min="0" placeholder="Années de pratique" value={form.annees_pratique} onChange={(e) => setForm({ ...form, annees_pratique: e.target.value })} className="hard-border px-3 py-2"/>

          <select data-testid="f-groupe" value={form.groupe_age} onChange={(e) => setForm({ ...form, groupe_age: e.target.value })} className="hard-border px-3 py-2 bg-white">
            {GROUPES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input data-testid="f-sessions" type="number" min="1" max="7" placeholder="Cours / semaine" value={form.sessions_per_week} onChange={(e) => setForm({ ...form, sessions_per_week: e.target.value })} className="hard-border px-3 py-2"/>
          <input data-testid="f-tarif" type="number" step="0.01" min="0" placeholder="Tarif mensuel ($)" value={form.tarif_mensuel} onChange={(e) => setForm({ ...form, tarif_mensuel: e.target.value })} className="hard-border px-3 py-2"/>

          <select data-testid="f-statut" value={form.statut} onChange={(e) => setForm({ ...form, statut: e.target.value })} className="hard-border px-3 py-2 bg-white">
            {STATUTS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
          </select>
          <input data-testid="f-email" type="email" placeholder="Courriel élève" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="hard-border px-3 py-2"/>
          <input data-testid="f-phone" placeholder="Téléphone élève" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="hard-border px-3 py-2"/>

          {/* ── DATES CLAIREMENT ÉTIQUETÉES (Lot 2) ── */}
          <div className="md:col-span-3 ticker text-xs text-muted-foreground mt-2" data-testid="dates-section-title">📅 Dates du contrat — bien distinctes</div>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground" data-testid="lbl-dob">
            🎂 Date de NAISSANCE de l'élève
            <input data-testid="f-dob" type="date" value={form.date_of_birth} onChange={(e) => setForm({ ...form, date_of_birth: e.target.value })} className="hard-border px-3 py-2 text-foreground"/>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground" data-testid="lbl-date-debut">
            ▶️ Date de DÉBUT du contrat
            <input data-testid="f-date-debut" type="date" value={form.date_debut} onChange={(e) => { const v = e.target.value; setForm((f) => ({ ...f, date_debut: v, date_fin: durationWeeks ? addWeeks(v, parseInt(durationWeeks)) : f.date_fin })); }} className="hard-border px-3 py-2 text-foreground"/>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground" data-testid="lbl-duration">
            ⏳ Durée → calcule la FIN auto.
            <select data-testid="f-duration" value={durationWeeks} onChange={(e) => { const w = e.target.value; setDurationWeeks(w); if (w && form.date_debut) setForm((f) => ({ ...f, date_fin: addWeeks(f.date_debut, parseInt(w)) })); }} className="hard-border px-3 py-2 bg-white text-foreground">
              <option value="">— choisir une durée —</option>
              {DURATIONS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
            </select>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground md:col-span-1" data-testid="lbl-date-fin">
            🏁 Date de FIN du contrat
            <input data-testid="f-date-fin" type="date" value={form.date_fin} onChange={(e) => setForm({ ...form, date_fin: e.target.value })} className="hard-border px-3 py-2 text-foreground"/>
          </label>
          <div className="md:col-span-2 flex items-end text-xs text-muted-foreground pb-2" data-testid="dates-helper">
            {form.date_debut && form.date_fin ? `Contrat du ${form.date_debut} au ${form.date_fin}.` : "Choisis la date de début + une durée → la fin se calcule toute seule."}
          </div>

          {/* Parents / Tuteurs — souvent séparés, on garde les 2 */}
          <div className="md:col-span-3 ticker text-xs text-muted-foreground mt-2" data-testid="parents-section-title">👪 Parents / Tuteurs (pour les élèves mineurs)</div>
          {form.parents.map((p, i) => (
            <div key={i} className="md:col-span-3 grid grid-cols-1 md:grid-cols-4 gap-2 hard-border p-3 bg-white" data-testid={`parent-block-${i}`}>
              <input placeholder={`Nom du parent ${i + 1}`} value={p.nom} data-testid={`f-parent${i}-nom`}
                onChange={(e) => { const a = [...form.parents]; a[i] = { ...a[i], nom: e.target.value }; setForm({ ...form, parents: a }); }} className="hard-border px-3 py-2"/>
              <select value={p.lien || ""} data-testid={`f-parent${i}-lien`}
                onChange={(e) => { const a = [...form.parents]; a[i] = { ...a[i], lien: e.target.value }; setForm({ ...form, parents: a }); }} className="hard-border px-3 py-2 bg-white">
                <option value="">Lien…</option>
                <option value="père">Père</option>
                <option value="mère">Mère</option>
                <option value="tuteur">Tuteur</option>
                <option value="tutrice">Tutrice</option>
                <option value="autre">Autre</option>
              </select>
              <input type="email" placeholder="Courriel" value={p.courriel} data-testid={`f-parent${i}-courriel`}
                onChange={(e) => { const a = [...form.parents]; a[i] = { ...a[i], courriel: e.target.value }; setForm({ ...form, parents: a }); }} className="hard-border px-3 py-2"/>
              <input placeholder="Cellulaire" value={p.cellulaire} data-testid={`f-parent${i}-cell`}
                onChange={(e) => { const a = [...form.parents]; a[i] = { ...a[i], cellulaire: e.target.value }; setForm({ ...form, parents: a }); }} className="hard-border px-3 py-2"/>
            </div>
          ))}

          {/* Contacts d'urgence */}
          <div className="md:col-span-3 ticker text-xs text-muted-foreground mt-2" data-testid="urgence-section-title">🚨 Contacts d'urgence (2)</div>
          {form.contacts_urgence.map((u, i) => (
            <div key={i} className="md:col-span-3 grid grid-cols-1 md:grid-cols-3 gap-2 hard-border p-3 bg-white" data-testid={`urgence-block-${i}`}>
              <input placeholder={`Nom contact urgence ${i + 1}`} value={u.nom} data-testid={`f-urgence${i}-nom`}
                onChange={(e) => { const a = [...form.contacts_urgence]; a[i] = { ...a[i], nom: e.target.value }; setForm({ ...form, contacts_urgence: a }); }} className="hard-border px-3 py-2"/>
              <input placeholder="Lien (grand-parent, voisin…)" value={u.lien} data-testid={`f-urgence${i}-lien`}
                onChange={(e) => { const a = [...form.contacts_urgence]; a[i] = { ...a[i], lien: e.target.value }; setForm({ ...form, contacts_urgence: a }); }} className="hard-border px-3 py-2"/>
              <input placeholder="Téléphone" value={u.telephone} data-testid={`f-urgence${i}-tel`}
                onChange={(e) => { const a = [...form.contacts_urgence]; a[i] = { ...a[i], telephone: e.target.value }; setForm({ ...form, contacts_urgence: a }); }} className="hard-border px-3 py-2"/>
            </div>
          ))}

          <textarea data-testid="f-notes" placeholder="Notes (allergies, objectifs, etc.)" value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="hard-border px-3 py-2 md:col-span-3" rows={2}/>

          <label className="flex items-center gap-2 text-xs md:col-span-3 cursor-pointer" data-testid="f-exempt-equipe-label">
            <input type="checkbox" data-testid="f-exempt-equipe" checked={!!form.exempt_frais_equipe} onChange={(e) => setForm({ ...form, exempt_frais_equipe: e.target.checked })}/>
            <span>Membre de l'équipe (coach / staff) — <b>exempt des frais d'inscription et de renouvellement</b> (exclu des rappels automatiques)</span>
          </label>

          <button data-testid="f-submit" type="submit" className="bg-foreground hover:bg-[#D90429] text-white py-2.5 ticker transition-colors md:col-span-3">
            {editingId ? "Mettre à jour le contrat" : "Créer le contrat"}
          </button>
        </form>
      )}

      {/* PROGRAMMES INTÉGRÉS (fusion 2026-10-14) */}
      {tab === "ps" && <div className="px-8 lg:px-12 py-8 bg-zinc-950" data-testid="view-ps"><PetitsSamouraisModule embedded /></div>}
      {tab === "pkh" && <div className="bg-zinc-950" data-testid="view-pkh"><PowerKickHerModule embedded /></div>}

      {/* LISTE ou CALENDRIER */}
      {tab === "liste" && (
      <div className="px-8 lg:px-12 py-8" data-testid="view-liste">
        {displayList.length === 0 ? (
          <div className="text-center py-16 text-muted-foreground" data-testid="empty-state">
            <Users size={48} className="mx-auto mb-4 opacity-30"/>
            <p>Aucun contrat élève pour le moment. Clique sur « Ajouter un élève » pour commencer.</p>
          </div>
        ) : (
          <>
          <div className="flex items-center justify-between gap-2 mb-2 text-xs text-muted-foreground">
            <span>Glisse le tableau (Maj + molette) ou utilise les flèches — la colonne Élève et les Actions restent visibles.</span>
            <div className="flex gap-1">
              <button type="button" onClick={() => tableScrollRef.current?.scrollBy({ left: -500, behavior: "smooth" })} className="hard-border px-3 py-1.5 hover:bg-secondary/40" data-testid="table-scroll-left" title="Défiler vers la gauche">◀</button>
              <button type="button" onClick={() => tableScrollRef.current?.scrollBy({ left: 500, behavior: "smooth" })} className="hard-border px-3 py-1.5 hover:bg-secondary/40" data-testid="table-scroll-right" title="Défiler vers la droite (Actions)">▶</button>
            </div>
          </div>
          <div className="overflow-x-auto hard-border" ref={tableScrollRef} style={{ scrollbarWidth: "thin" }}>
            <table className="w-full text-sm" data-testid="contracts-table">
              <thead className="bg-secondary/40 ticker">
                <tr>
                  <th className="text-center px-3 py-3"><input type="checkbox" data-testid="select-all-checkbox" onChange={(e) => setSelectedIds(e.target.checked ? displayList.map((c) => c.id) : [])} checked={displayList.length > 0 && selectedIds.length === displayList.length} /></th>
                  <th className="text-left px-4 py-3 sticky left-0 z-10 bg-card">Élève</th>
                  <th className="text-left px-4 py-3">Discipline</th>
                  <th className="text-left px-4 py-3">Ceinture</th>
                  <th className="text-left px-4 py-3">Âge / Groupe</th>
                  <th className="text-left px-4 py-3">Fréq.</th>
                  <th className="text-right px-4 py-3">Tarif/mois</th>
                  <th className="text-center px-4 py-3">Signature</th>
                  <th className="text-center px-4 py-3">Équipement</th>
                  <th className="text-center px-4 py-3">Statut</th>
                  <th className="text-right px-4 py-3 sticky right-0 z-10 bg-card">Actions</th>
                </tr>
              </thead>
              <tbody>
                {displayList.map((c) => {
                  const disciplineLabel = DISCIPLINES.find(([v]) => v === c.discipline)?.[1] ?? c.discipline;
                  const secondaryLabel = c.secondary_discipline ? (DISCIPLINES.find(([v]) => v === c.secondary_discipline)?.[1] ?? c.secondary_discipline) : null;
                  const groupeLabel = GROUPES.find(([v]) => v === c.groupe_age)?.[1] ?? "—";
                  const statusColor =
                    c.statut === "active" ? "bg-[#10b981]/15 text-[#10b981]" :
                    c.statut === "essai" ? "bg-[#3b82f6]/15 text-[#3b82f6]" :
                    c.statut === "suspendu" ? "bg-[#f59e0b]/15 text-[#f59e0b]" :
                    "bg-muted text-muted-foreground";
                  return (
                    <tr key={c.id} className={`hard-border-t hover:bg-secondary/20 ${selectedIds.includes(c.id) ? "bg-[#3b82f6]/10" : !c.tarif_mensuel ? "bg-[#D90429]/5" : ""}`} data-testid={`row-${c.id}`}>
                      <td className="px-3 py-3 text-center"><input type="checkbox" data-testid={`select-${c.id}`} checked={selectedIds.includes(c.id)} onChange={() => toggleSelect(c.id)} /></td>
                      <td className="px-4 py-3 font-medium sticky left-0 z-10 bg-card shadow-[4px_0_8px_-4px_rgba(0,0,0,.6)]">{c.full_name}{(c.parent_name || c.parents?.[0]?.nom) && <div className="text-[11px] font-normal text-muted-foreground" data-testid={`parent-name-${c.id}`}>👨‍👩‍👧 {c.parent_name || c.parents?.[0]?.nom}{(c.parent_phone || c.parents?.[0]?.cellulaire) ? ` · ${c.parent_phone || c.parents?.[0]?.cellulaire}` : ""}</div>}{c.en_retard && <span className="ml-2 text-[10px] bg-[#D90429] text-white px-1.5 py-0.5 rounded">RETARD</span>}{c.billing_status === "en_pause" && <span className="ml-2 text-[10px] bg-sky-700 text-white px-1.5 py-0.5 rounded whitespace-nowrap" title={`Abonnement Stripe en pause${c.paused_since ? ` depuis le ${c.paused_since}` : ""}`} data-testid={`pause-badge-${c.id}`}>⏸ EN PAUSE</span>}{!c.tarif_mensuel && <span className="ml-2 text-[10px] bg-[#D90429] text-white px-1.5 py-0.5 rounded" data-testid={`no-tarif-badge-${c.id}`}>TARIF MANQUANT</span>}{dupIds.includes(c.id) && <span className="ml-2 text-[10px] bg-[#7c3aed] text-white px-1.5 py-0.5 rounded cursor-help" title="Même courriel avec 2 abonnements Stripe actifs — cochez les deux contrats puis « Fusionner »" data-testid={`dup-badge-${c.id}`}>⚠ DOUBLON PROBABLE</span>}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1.5 flex-wrap">
                          <span>{disciplineLabel}</span>
                          {secondaryLabel && (
                            <span
                              data-testid={`multidiscipline-badge-${c.id}`}
                              title={`Multi-disciplines (${disciplineLabel} + ${secondaryLabel}) → rabais −25% sur l'équipement`}
                              className="ticker text-[10px] bg-[#10b981]/15 text-[#10b981] px-1.5 py-0.5 rounded font-bold"
                            >
                              −25% KIT
                            </span>
                          )}
                        </div>
                        {secondaryLabel && <span className="text-xs text-muted-foreground">+ {secondaryLabel}</span>}
                      </td>
                      <td className="px-4 py-3 text-muted-foreground">{c.niveau_ceinture || "—"}{c.annees_pratique != null && ` (${c.annees_pratique} an${c.annees_pratique > 1 ? "s" : ""})`}</td>
                      <td className="px-4 py-3 text-muted-foreground">{c.age ? `${c.age} ans` : "—"}<br/><span className="text-xs">{groupeLabel}</span></td>
                      <td className="px-4 py-3 text-center font-mono">{c.sessions_per_week}×/sem</td>
                      <td className="px-4 py-3 text-right font-mono" data-testid={`tarif-cell-${c.id}`}>
                        {editingTarif === c.id ? (
                          <span className="inline-flex items-center gap-1">
                            <input autoFocus type="number" step="0.01" min="0" value={tarifDraft}
                              data-testid={`tarif-input-${c.id}`}
                              onChange={(e) => setTarifDraft(e.target.value)}
                              onKeyDown={(e) => { if (e.key === "Enter") saveTarif(c); if (e.key === "Escape") setEditingTarif(null); }}
                              className="w-20 hard-border px-2 py-1 text-right bg-white text-black" />
                            <button data-testid={`tarif-save-${c.id}`} onClick={() => saveTarif(c)} className="text-[#10b981] hover:bg-[#10b981]/10 p-1" title="Enregistrer"><CheckCircle2 size={15}/></button>
                          </span>
                        ) : (
                          <button data-testid={`tarif-edit-${c.id}`} onClick={() => startEditTarif(c)}
                            className={`hover:underline ${c.tarif_mensuel ? "" : "text-[#D90429] font-bold"}`}
                            title="Cliquer pour modifier le tarif">
                            {c.tarif_mensuel ? `${c.tarif_mensuel.toFixed(2)} $` : "＋ tarif"}
                          </button>
                        )}
                        {c.discount && <div className="text-[10px] text-emerald-400 whitespace-nowrap" title={`${c.discount.label} · ${c.discount.reason || ""}${c.discount.applied_to_stripe ? " · appliqué sur Stripe" : " · " + (c.discount.stripe_note || "")}`} data-testid={`discount-badge-${c.id}`}>🏷️ {c.discount.label} → {Number(c.discount.tarif_net).toFixed(2)} $</div>}
                      </td>
                      <td className="px-4 py-3 text-center"><EquipmentTick contract={c} onChanged={load} /></td>
                      <td className="px-4 py-3 text-center" data-testid={`sign-status-${c.id}`}>
                        {c.contract_signed_at ? (
                          <span className="inline-flex items-center gap-1 text-[11px] text-[#10b981]" title={`Signé le ${c.contract_signed_at?.slice(0,10)}${c.signed_name ? " par " + c.signed_name : ""}`}>
                            <CheckCircle2 size={13}/> Signé
                            {c.signed_contract_url && (
                              <a href={`${apiBase}${c.signed_contract_url}`} target="_blank" rel="noreferrer" data-testid={`download-signed-${c.id}`} className="ml-1 text-[#10b981] hover:text-emerald-300" title="Télécharger le contrat signé (PDF)"><Download size={13}/></a>
                            )}
                          </span>
                        ) : c.contract_sent_at ? (
                          <span className="text-[11px] text-amber-500" title={`Envoyé le ${c.contract_sent_at?.slice(0,10)}`}>⏳ En attente</span>
                        ) : (
                          <span className="text-[11px] text-muted-foreground">—</span>
                        )}
                      </td>
                      <td className="px-4 py-3 text-center"><span className={`ticker px-2 py-1 ${statusColor}`}>{c.statut}</span>{c.cancellation && c.statut !== "résilié" && <span className="block mt-1 text-[9px] text-[#D90429]" data-testid={`cancel-badge-${c.id}`} title={`Résiliation planifiée — fin ${c.cancellation.effective_date}`}>RÉSIL. {c.cancellation.effective_date}</span>}</td>
                      <td className="px-3 py-3 sticky right-0 z-10 bg-card shadow-[-4px_0_8px_-4px_rgba(0,0,0,.6)]">
                        <div className="flex flex-wrap justify-end gap-1.5 w-[300px] xl:w-[440px] ml-auto" data-testid={`actions-${c.id}`}>
                          <ActionBtn testId={`hb-${c.id}`} icon={HeartStraight} label="Santé" tone={TONE.gold} onClick={() => setHbContract(c)} title="Carnet de santé" />
                          <ActionBtn testId={`charge-${c.id}`} icon={Receipt} label="Facturer" tone={TONE.red} onClick={() => chargeMonth(c)} disabled={!c.tarif_mensuel} title="Facturer le mois (paiement unique)" />
                          <ActionBtn testId={`sublink-${c.id}`} icon={ArrowsClockwise} label="Abonnement" tone={TONE.green} onClick={() => subscriptionLink(c)} disabled={!c.tarif_mensuel} title="Lien d'abonnement mensuel automatique (à envoyer à l'élève)" />
                          <ActionBtn testId={`paylink-dojo-${c.id}`} icon={PaperPlaneTilt} label="Lien $" tone={TONE.violet} onClick={() => setPayLinkContract(c)} title="Envoyer un lien de paiement (Tarif Dojo → Stripe) par SMS ou courriel" />
                          <ActionBtn testId={`link-sub-${c.id}`} icon={ArrowsClockwise} label="Lier Stripe" tone={TONE.violet} onClick={async () => {
                            const sid = window.prompt(`Relier ${c.full_name} à son abonnement Stripe actif.\nLaisse vide pour détecter automatiquement par courriel, ou colle l'identifiant sub_…`, "");
                            if (sid === null) return;
                            try { const r = await api.post(`/student-contracts/${c.id}/link-stripe-subscription`, { subscription_id: sid.trim() || null }); toast.success(`Lié à ${r.data.subscription_id} (${r.data.status}) · ${r.data.amount ?? "?"} $ / ${r.data.interval || "?"}`); load(); }
                            catch (e) { toast.error(e?.response?.data?.detail || "Erreur"); }
                          }} title="Relier ce contrat à l'abonnement Stripe actif (détection par courriel ou identifiant)" />
                          <ActionBtn testId={`discount-${c.id}`} icon={Tag} label="Rabais" tone={TONE.gold} onClick={() => setDiscountContract(c)} disabled={!c.tarif_mensuel} title="Rabais / pourcentage client spécial (coupon Stripe sur l'abonnement)" />
                          <ActionBtn testId={`send-contract-${c.id}`} icon={Signature} label="Contrat" tone={TONE.blue} onClick={() => sendContractPayment(c)} title="Envoyer le contrat (PDF) + le lien de paiement en un seul courriel" />
                          <ActionBtn testId={`confirm-activated-${c.id}`} icon={SealCheck} label="Activé" tone={TONE.green} onClick={() => confirmActivated(c)} title="Confirmer activé (payé en personne / virement)" />
                          <ActionBtn testId={`welcome-${c.id}`} icon={HandHeart} label="Bienvenue" tone={TONE.pink} onClick={() => openWelcome(c)} title="Mot de bienvenue — Ambassadeur KAIZEN" />
                          <ActionBtn testId={`carnet-${c.id}`} icon={BookOpenText} label="Carnet" tone={TONE.gold} onClick={() => navigate(`/app/carnet-progression?student=${encodeURIComponent(c.id)}&name=${encodeURIComponent(c.full_name || "")}&age=${c.age || ""}&discipline=${encodeURIComponent(c.discipline || "")}`)} title="Ouvrir le carnet de progression de cet élève (créé au besoin)" />
                          <ActionBtn testId={`edit-${c.id}`} icon={PencilSimpleLine} label="Modifier" tone={TONE.zinc} onClick={() => edit(c)} title="Modifier la fiche" />
                          {c.cancellation && c.statut !== "résilié" ? (
                            <ActionBtn testId={`revoke-${c.id}`} icon={ArrowCounterClockwise} label="Réactiver" tone={TONE.green} onClick={() => revokeCancellation(c)} title="Annuler la résiliation (réactiver)" />
                          ) : (
                            <ActionBtn testId={`cancel-${c.id}`} icon={Prohibit} label="Résilier" tone={TONE.orange} onClick={() => setCancelContract(c)} title="Résilier (préavis + pénalité)" />
                          )}
                          <ActionBtn testId={`del-${c.id}`} icon={TrashSimple} label="Supprimer" tone={TONE.red} onClick={() => remove(c.id, c.full_name)} title="Supprimer le contrat" />
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          </>
        )}
      </div>
      )}

      {/* VUE CALENDRIER DES RENOUVELLEMENTS */}
      {tab === "calendar" && (
        <div className="px-8 lg:px-12 py-8" data-testid="view-calendar">
          {!calendar || (calendar.mois_tries.length === 0 && calendar.sans_date.length === 0) ? (
            <div className="text-center py-16 text-muted-foreground">
              <CalendarDays size={48} className="mx-auto mb-4 opacity-30"/>
              <p>Aucun renouvellement à venir. Renseigne les <strong>dates de prochain paiement</strong> dans les fiches élèves pour activer le calendrier.</p>
            </div>
          ) : (
            <div className="space-y-8">
              {calendar.total_mensuel_attendu > 0 && (
                <div className="hard-border p-4 bg-secondary/20 flex items-center justify-between">
                  <div>
                    <div className="ticker text-muted-foreground mb-1">REVENUS ATTENDUS · TOUS MOIS PROGRAMMÉS</div>
                    <div className="text-3xl font-black font-mono text-impact-gold">{calendar.total_mensuel_attendu.toFixed(2)} $</div>
                  </div>
                  <CalendarDays size={40} className="text-impact-gold opacity-50"/>
                </div>
              )}

              {calendar.mois_tries.map((moisKey) => {
                const entries = calendar.par_mois[moisKey] || [];
                const total = entries.reduce((acc, e) => acc + (e.billing_cycle === "mensuel" ? e.tarif_mensuel : 0), 0);
                const [annee, mois] = moisKey.split("-");
                const moisLabel = new Date(parseInt(annee), parseInt(mois) - 1).toLocaleDateString("fr-CA", { month: "long", year: "numeric" });
                return (
                  <div key={moisKey} className="hard-border" data-testid={`month-${moisKey}`}>
                    <div className="px-4 py-3 bg-foreground text-white flex items-center justify-between">
                      <h3 className="font-display font-black uppercase tracking-wider">{moisLabel}</h3>
                      <div className="ticker">{entries.length} renouvellement{entries.length > 1 ? "s" : ""} · <span className="text-impact-gold font-mono ml-2">{total.toFixed(2)} $</span></div>
                    </div>
                    <table className="w-full text-sm">
                      <tbody>
                        {entries.map((e) => {
                          const disciplineLabel = DISCIPLINES.find(([v]) => v === e.discipline)?.[1] ?? e.discipline;
                          return (
                            <tr key={e.id} className="hard-border-t">
                              <td className="px-4 py-2.5 font-mono text-muted-foreground w-32">{e.prochain_paiement_du}</td>
                              <td className="px-4 py-2.5 font-medium">{e.full_name}{e.en_retard && <span className="ml-2 text-[10px] bg-[#D90429] text-white px-1.5 py-0.5 rounded">RETARD</span>}</td>
                              <td className="px-4 py-2.5 text-muted-foreground">{disciplineLabel}</td>
                              <td className="px-4 py-2.5 text-right font-mono">{e.tarif_mensuel.toFixed(2)} $</td>
                              <td className="px-4 py-2.5 text-xs uppercase text-muted-foreground w-24 text-right">{e.billing_cycle}</td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                );
              })}

              {calendar.sans_date.length > 0 && (
                <div className="hard-border" data-testid="sans-date-section">
                  <div className="px-4 py-3 bg-[#f59e0b]/15 text-[#f59e0b] flex items-center gap-2">
                    <AlertTriangle size={16}/>
                    <h3 className="font-display font-bold uppercase tracking-wider">Sans date de prochain paiement ({calendar.sans_date.length})</h3>
                  </div>
                  <table className="w-full text-sm">
                    <tbody>
                      {calendar.sans_date.map((e) => {
                        const disciplineLabel = DISCIPLINES.find(([v]) => v === e.discipline)?.[1] ?? e.discipline;
                        return (
                          <tr key={e.id} className="hard-border-t">
                            <td className="px-4 py-2.5 font-medium">{e.full_name}</td>
                            <td className="px-4 py-2.5 text-muted-foreground">{disciplineLabel}</td>
                            <td className="px-4 py-2.5 text-right font-mono">{e.tarif_mensuel.toFixed(2)} $</td>
                            <td className="px-4 py-2.5 text-xs italic text-muted-foreground text-right">à renseigner</td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          )}
        </div>
      )}

      {/* DRAWER CARNET DE SANTÉ */}
      {hbContract && (
        <HealthBookDrawer
          contract={hbContract}
          onClose={() => setHbContract(null)}
          onContractUpdated={() => { load(); setHbContract(null); }}
        />
      )}
      {/* MODALE RÉSILIATION (admin) — Lot 6 */}
      {cancelContract && (
        <CancelContractModal
          contract={cancelContract}
          onClose={() => setCancelContract(null)}
          onDone={() => { load(); setCancelContract(null); }}
        />
      )}
      {discountContract && <DiscountModal contract={discountContract} onClose={() => setDiscountContract(null)} onDone={load} />}
      {/* MODALE ENVOI LIEN DE PAIEMENT (Tarif Dojo → Stripe) */}
      {payLinkContract && (
        <PayLinkModal
          contract={payLinkContract}
          onClose={() => setPayLinkContract(null)}
        />
      )}
      {/* MODALE BIENVENUE — Ambassadeur KAIZEN */}      {welcomeContract && (
        <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="welcome-modal" onClick={() => setWelcomeContract(null)}>
          <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 max-w-md w-full" onClick={(e) => e.stopPropagation()}>
            <div className="text-amber-400 text-[10px] tracking-[0.3em] uppercase mb-1">💛 Mot de bienvenue</div>
            <h3 className="text-lg font-bold text-white mb-1">Bienvenue pour {welcomeContract.full_name}</h3>
            <p className="text-zinc-500 text-xs mb-4">Un ambassadeur KAIZEN enverra un mot de bienvenue par courriel + une carte dans l'Espace Membre.</p>
            <div className="text-[10px] tracking-[0.25em] text-zinc-400 mb-2">CHOISIR L'AMBASSADEUR</div>
            <div className="grid grid-cols-3 gap-2 mb-5">
              {AMBASSADORS.map((a) => (
                <button key={a.key} data-testid={`welcome-amb-${a.key}`} onClick={() => setWelcomeAmb(a.key)}
                  className={`rounded-xl border p-3 text-center transition ${welcomeAmb === a.key ? "border-2" : "border-zinc-800 hover:border-zinc-600"}`}
                  style={welcomeAmb === a.key ? { borderColor: a.accent } : {}}>
                  <div className="font-black tracking-widest text-sm" style={{ color: a.accent }}>{a.name}</div>
                  <div className="text-[10px] text-zinc-400 mt-1 leading-tight">{a.tagline}</div>
                </button>
              ))}
            </div>
            <div className="flex gap-2">
              <button data-testid="welcome-send-btn" onClick={sendWelcome} disabled={welcomeSending}
                className="flex-1 bg-amber-500 hover:bg-amber-400 text-black font-bold py-2.5 rounded-lg disabled:opacity-50">
                {welcomeSending ? "Envoi…" : "Envoyer la bienvenue"}
              </button>
              <button data-testid="welcome-close-btn" onClick={() => setWelcomeContract(null)}
                className="px-4 py-2.5 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-900">Annuler</button>
            </div>
          </div>
        </div>
      )}

      {tplOpen && (
        <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" data-testid="contract-template-modal">
          <div className="bg-background hard-border w-full max-w-lg p-6">
            <div className="flex items-center gap-2 mb-1"><FileText size={18}/><h3 className="font-display font-black text-xl">Modèle de contrat</h3></div>
            <p className="text-sm text-muted-foreground mb-4">Une condition par ligne. Ce texte apparaît sur la page de signature et dans le PDF signé.</p>
            <textarea
              data-testid="template-textarea"
              value={tplConditions}
              onChange={(e) => setTplConditions(e.target.value)}
              rows={10}
              className="w-full hard-border px-3 py-2 bg-white text-black text-sm font-mono"
              placeholder="Une condition par ligne…"
            />
            <div className="flex justify-end gap-2 mt-4">
              <button data-testid="template-cancel-btn" onClick={() => setTplOpen(false)} className="px-4 py-2 border border-border hover:bg-secondary text-sm">Annuler</button>
              <button data-testid="template-save-btn" onClick={saveTemplate} className="px-4 py-2 bg-foreground text-white text-sm">Enregistrer</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}/* ─────── Résiliation contrôlée par l'admin : préavis + pénalité (Lot 6) ─────── */
function CancelContractModal({ contract, onClose, onDone }) {
  const [noticeWeeks, setNoticeWeeks] = useState(4);
  const [penalty, setPenalty] = useState(0);
  const [reason, setReason] = useState("");
  const [immediate, setImmediate] = useState(false);
  const [notified, setNotified] = useState(true);
  const [busy, setBusy] = useState(false);
  const today = new Date();
  const eff = immediate ? today : new Date(today.getTime() + noticeWeeks * 7 * 86400000);
  const effStr = eff.toISOString().slice(0, 10);

  const submit = async () => {
    setBusy(true);
    try {
      await api.post(`/student-contracts/${contract.id}/cancel`, {
        reason, notice_weeks: Number(noticeWeeks), penalty_amount: Number(penalty),
        immediate, notified_by_email: notified,
      });
      toast.success(immediate ? "Contrat résilié immédiatement." : `Résiliation planifiée — fin ${effStr}.`);
      onDone();
    } catch { toast.error("Erreur lors de la résiliation."); setBusy(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/70 flex items-center justify-center p-4" onClick={onClose} data-testid="cancel-modal">
      <div className="bg-white hard-border w-full max-w-md p-6" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center gap-2 text-[#D90429] mb-1"><Ban size={18}/><h3 className="font-display font-black text-xl">Résilier le contrat</h3></div>
        <p className="text-sm text-muted-foreground mb-4">{contract.full_name} — politique : préavis écrit de 4 semaines + pénalité éventuelle. Action réservée à l'admin.</p>
        <div className="space-y-3">
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">Préavis (semaines)
            <input data-testid="cancel-notice-weeks" type="number" min={0} value={noticeWeeks} disabled={immediate} onChange={(e) => setNoticeWeeks(e.target.value)} className="hard-border px-3 py-2 text-foreground disabled:opacity-50"/>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">Pénalité ($)
            <input data-testid="cancel-penalty" type="number" min={0} step="0.01" value={penalty} onChange={(e) => setPenalty(e.target.value)} className="hard-border px-3 py-2 text-foreground"/>
          </label>
          <label className="flex flex-col gap-1 text-xs text-muted-foreground">Raison
            <textarea data-testid="cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} rows={2} className="hard-border px-3 py-2 text-foreground" placeholder="Déménagement, blessure, etc."/>
          </label>
          <label className="flex items-center gap-2 text-sm"><input data-testid="cancel-notified" type="checkbox" checked={notified} onChange={(e) => setNotified(e.target.checked)} className="w-4 h-4 accent-[#D90429]"/> Le client a notifié par courriel</label>
          <label className="flex items-center gap-2 text-sm"><input data-testid="cancel-immediate" type="checkbox" checked={immediate} onChange={(e) => setImmediate(e.target.checked)} className="w-4 h-4 accent-[#D90429]"/> Résiliation immédiate (sans préavis)</label>
          <div className="text-xs bg-secondary/40 px-3 py-2" data-testid="cancel-effective">Date effective de fin : <strong>{effStr}</strong></div>
        </div>
        <div className="flex gap-2 mt-5">
          <button data-testid="cancel-confirm-btn" onClick={submit} disabled={busy} className="flex-1 bg-[#D90429] text-white py-2.5 ticker hover:bg-[#BF0424] disabled:opacity-50">{busy ? "..." : (immediate ? "Résilier maintenant" : "Planifier la résiliation")}</button>
          <button data-testid="cancel-close-btn" onClick={onClose} className="hard-border px-4 py-2.5 ticker hover:bg-secondary/40">Fermer</button>
        </div>
      </div>
    </div>
  );
}


function PayLinkModal({ contract, onClose }) {
  const [all, setAll] = useState([]);
  const [q, setQ] = useState("");
  const [tariffId, setTariffId] = useState("");
  const [channel, setChannel] = useState(contract?.email || contract?.parent_email ? "email" : "sms");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get("/dojo-tariffs").then((r) => {
      const synced = (r.data || []).filter((t) => t.stripe_payment_url && t.active);
      setAll(synced);
    }).catch(() => {});
  }, []);

  const filtered = all.filter((t) => {
    const s = q.trim().toLowerCase();
    return !s || (t.label || "").toLowerCase().includes(s) || (t.program_id || "").toLowerCase().includes(s);
  });
  const chosen = all.find((t) => t.id === tariffId);
  const hasEmail = !!(contract?.email || contract?.parent_email);
  const hasPhone = !!(contract?.phone || contract?.parent_phone);

  const send = async () => {
    if (!tariffId) { toast.error("Choisis un tarif."); return; }
    setBusy(true);
    try {
      const r = await api.post(`/student-contracts/${contract.id}/send-payment-link`, { tariff_id: tariffId, channel });
      toast.success(`Lien envoyé (${r.data.channel}) à ${r.data.sent_to}`);
      onClose();
    } catch (e) { toast.error(e?.response?.data?.detail || "Échec de l'envoi."); }
    setBusy(false);
  };

  return (
    <div className="fixed inset-0 bg-black/70 z-50 flex items-center justify-center p-4" data-testid="paylink-modal" onClick={onClose}>
      <div className="bg-zinc-950 border border-zinc-800 rounded-2xl p-6 max-w-md w-full relative" onClick={(e) => e.stopPropagation()}>
        <button onClick={onClose} data-testid="paylink-close" aria-label="Fermer"
          className="absolute top-3 right-3 text-zinc-500 hover:text-white text-xl leading-none w-8 h-8 flex items-center justify-center rounded-lg hover:bg-zinc-800">×</button>
        <div className="text-[#635BFF] text-[10px] tracking-[0.3em] uppercase mb-1">💳 Lien de paiement</div>
        <h3 className="text-lg font-bold text-white mb-1">Envoyer à {contract.full_name}</h3>
        <p className="text-zinc-500 text-xs mb-4">Choisis un tarif Dojo synchronisé et le canal d'envoi.</p>

        {all.length === 0 ? (
          <div className="text-amber-400 text-sm bg-amber-500/10 border border-amber-500/30 rounded-lg px-3 py-2">
            Aucun tarif synchronisé avec Stripe. Lance d'abord « Synchroniser vers Stripe » dans Tarifs Dojo.
          </div>
        ) : (
          <>
            <input data-testid="paylink-search" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un tarif…"
              className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-3 py-2 text-sm text-white mb-2" />
            <select data-testid="paylink-select" value={tariffId} onChange={(e) => setTariffId(e.target.value)}
              size={6} className="w-full bg-zinc-900 border border-zinc-800 rounded-lg px-2 py-2 text-sm text-white">
              <option value="">— Choisir un tarif —</option>
              {filtered.map((t) => (
                <option key={t.id} value={t.id}>{t.label} · {t.price}$ ({t.billing_cycle})</option>
              ))}
            </select>

            <div className="flex gap-2 mt-3">
              <button data-testid="paylink-channel-email" disabled={!hasEmail} onClick={() => setChannel("email")}
                className={`flex-1 py-2 text-sm rounded-lg border ${channel === "email" ? "bg-[#635BFF] text-white border-[#635BFF]" : "border-zinc-700 text-zinc-300"} disabled:opacity-40`}>
                Courriel {hasEmail ? "" : "(aucun)"}
              </button>
              <button data-testid="paylink-channel-sms" disabled={!hasPhone} onClick={() => setChannel("sms")}
                className={`flex-1 py-2 text-sm rounded-lg border ${channel === "sms" ? "bg-[#635BFF] text-white border-[#635BFF]" : "border-zinc-700 text-zinc-300"} disabled:opacity-40`}>
                SMS {hasPhone ? "" : "(aucun)"}
              </button>
            </div>
            {chosen && <div className="text-xs text-zinc-500 mt-2 break-all">Lien : {chosen.stripe_payment_url}</div>}

            <div className="flex gap-2 mt-5">
              <button data-testid="paylink-send-btn" onClick={send} disabled={busy || !tariffId}
                className="flex-1 bg-[#635BFF] text-white py-2.5 rounded-lg font-semibold hover:bg-[#514dcc] disabled:opacity-50">
                {busy ? "Envoi…" : "Envoyer le lien"}
              </button>
              <button onClick={onClose} className="hard-border px-4 py-2.5 text-zinc-300 hover:bg-zinc-800 rounded-lg">Fermer</button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
